import { Config } from 'tailwindcss';
import { blur } from './blur';
import * as color from './color';
import { elevation } from './elevation';
import * as palette from './palette';
import { prose_css_defaults } from './prose';
import { radius } from './radius';
import { screen } from './screen';
import { space } from './space';
import { TokenOptions } from './types';
import { fontFamily, fontSize, fontWeight, lineHeight } from './typography';

const paletteColor = (options: TokenOptions) => ({
  blue: palette.blue.toTailwindConfig(options),
  gray: palette.gray.toTailwindConfig(options),
  green: palette.green.toTailwindConfig(options),
  orange: palette.orange.toTailwindConfig(options),
  pink: palette.pink.toTailwindConfig(options),
  purple: palette.purple.toTailwindConfig(options),
  red: palette.red.toTailwindConfig(options),
  yellow: palette.yellow.toTailwindConfig(options),
});

export const theme = {
  colors: {},
  backgroundColor: {
    DEFAULT: color.surface.get('default').toPropertyRef({ fallback: true }),
    ...color.surface.toTailwindConfig({ fallback: true }),
    border: color.border.toTailwindConfig({ fallback: true }),
    content: color.content.toTailwindConfig({ fallback: true }),
    button: color.button.toTailwindConfig({ fallback: true }),
    alpha: color.alpha.toTailwindConfig({ fallback: true }),
    avatar: color.avatar.toTailwindConfig({ fallback: true }),
    tag: color.tag.toTailwindConfig({ fallback: true }),
    overlay: color.overlay.toTailwindConfig({ fallback: true }),
    shadow: color.shadow.toPropertyRef({ fallback: true }),
    focus: color.focus.toPropertyRef({ fallback: true }),
    palette: paletteColor({ fallback: true }),
    inherit: 'inherit',
    current: 'currentColor',
    transparent: 'transparent',
  },
  gradientColorStops: ({ theme }) => theme('backgroundColor'),
  borderColor: {
    DEFAULT: color.border.get('default').toPropertyRef({ fallback: true }),
    ...color.border.toTailwindConfig({ fallback: true }),
    surface: color.surface.toTailwindConfig({ fallback: true }),
    content: color.content.toTailwindConfig({ fallback: true }),
    button: color.button.toTailwindConfig({ fallback: true }),
    alpha: color.alpha.toTailwindConfig({ fallback: true }),
    avatar: color.avatar.toTailwindConfig({ fallback: true }),
    tag: color.tag.toTailwindConfig({ fallback: true }),
    overlay: color.overlay.toTailwindConfig({ fallback: true }),
    shadow: color.shadow.toPropertyRef({ fallback: true }),
    focus: color.focus.toPropertyRef({ fallback: true }),
    palette: paletteColor({ fallback: true }),
    inherit: 'inherit',
    current: 'currentColor',
    transparent: 'transparent',
  },
  outlineColor: {
    DEFAULT: color.border.get('default').toPropertyRef({ fallback: true }),
    ...color.border.toTailwindConfig({ fallback: true }),
    focus: color.focus.toPropertyRef({ fallback: true }),
    surface: color.surface.toTailwindConfig({ fallback: true }),
    content: color.content.toTailwindConfig({ fallback: true }),
    button: color.button.toTailwindConfig({ fallback: true }),
    alpha: color.alpha.toTailwindConfig({ fallback: true }),
    avatar: color.avatar.toTailwindConfig({ fallback: true }),
    tag: color.tag.toTailwindConfig({ fallback: true }),
    overlay: color.overlay.toTailwindConfig({ fallback: true }),
    shadow: color.shadow.toPropertyRef({ fallback: true }),
    palette: paletteColor({ fallback: true }),
    inherit: 'inherit',
    current: 'currentColor',
    transparent: 'transparent',
  },
  textColor: {
    DEFAULT: color.content.get('default').toPropertyRef({ fallback: true }),
    ...color.content.toTailwindConfig({ fallback: true }),
    border: color.border.toTailwindConfig({ fallback: true }),
    surface: color.surface.toTailwindConfig({ fallback: true }),
    button: color.button.toTailwindConfig({ fallback: true }),
    alpha: color.alpha.toTailwindConfig({ fallback: true }),
    avatar: color.avatar.toTailwindConfig({ fallback: true }),
    tag: color.tag.toTailwindConfig({ fallback: true }),
    overlay: color.overlay.toTailwindConfig({ fallback: true }),
    shadow: color.shadow.toPropertyRef({ fallback: true }),
    focus: color.focus.toPropertyRef({ fallback: true }),
    palette: paletteColor({ fallback: true }),
    inherit: 'inherit',
    transparent: 'transparent',
  },
  textDecorationColor: {
    ...color.content.toTailwindConfig({ fallback: true }),
    border: color.border.toTailwindConfig({ fallback: true }),
    surface: color.surface.toTailwindConfig({ fallback: true }),
    button: color.button.toTailwindConfig({ fallback: true }),
    alpha: color.alpha.toTailwindConfig({ fallback: true }),
    avatar: color.avatar.toTailwindConfig({ fallback: true }),
    tag: color.tag.toTailwindConfig({ fallback: true }),
    overlay: color.overlay.toTailwindConfig({ fallback: true }),
    shadow: color.shadow.toPropertyRef({ fallback: true }),
    focus: color.focus.toPropertyRef({ fallback: true }),
    palette: paletteColor({ fallback: true }),
    inherit: 'inherit',
    current: 'currentColor',
    transparent: 'transparent',
  },
  caretColor: {
    content: color.content.toTailwindConfig({ fallback: true }),
    border: color.border.toTailwindConfig({ fallback: true }),
    surface: color.surface.toTailwindConfig({ fallback: true }),
    button: color.button.toTailwindConfig({ fallback: true }),
    alpha: color.alpha.toTailwindConfig({ fallback: true }),
    avatar: color.avatar.toTailwindConfig({ fallback: true }),
    tag: color.tag.toTailwindConfig({ fallback: true }),
    overlay: color.overlay.toTailwindConfig({ fallback: true }),
    shadow: color.shadow.toPropertyRef({ fallback: true }),
    focus: color.focus.toPropertyRef({ fallback: true }),
    palette: paletteColor({ fallback: true }),
    inherit: 'inherit',
    current: 'currentColor',
    transparent: 'transparent',
  },
  accentColor: {},
  boxShadowColor: {},
  fill: {
    surface: color.surface.toTailwindConfig({ fallback: true }),
    border: color.border.toTailwindConfig({ fallback: true }),
    content: color.content.toTailwindConfig({ fallback: true }),
    button: color.button.toTailwindConfig({ fallback: true }),
    alpha: color.alpha.toTailwindConfig({ fallback: true }),
    avatar: color.avatar.toTailwindConfig({ fallback: true }),
    tag: color.tag.toTailwindConfig({ fallback: true }),
    palette: paletteColor({ fallback: true }),
    inherit: 'inherit',
    current: 'currentColor',
    transparent: 'transparent',
  },
  stroke: {
    border: color.border.toTailwindConfig({ fallback: true }),
    focus: color.focus.toPropertyRef({ fallback: true }),
    surface: color.surface.toTailwindConfig({ fallback: true }),
    content: color.content.toTailwindConfig({ fallback: true }),
    button: color.button.toTailwindConfig({ fallback: true }),
    alpha: color.alpha.toTailwindConfig({ fallback: true }),
    avatar: color.avatar.toTailwindConfig({ fallback: true }),
    tag: color.tag.toTailwindConfig({ fallback: true }),
    overlay: color.overlay.toTailwindConfig({ fallback: true }),
    shadow: color.shadow.toPropertyRef({ fallback: true }),
    palette: paletteColor({ fallback: true }),
    inherit: 'inherit',
    current: 'currentColor',
    transparent: 'transparent',
  },
  ringColor: {
    DEFAULT: color.border.get('default').toPropertyRef({ fallback: true }),
    ...color.border.toTailwindConfig({ fallback: true }),
    focus: color.focus.toPropertyRef({ fallback: true }),
    surface: color.surface.toTailwindConfig({ fallback: true }),
    content: color.content.toTailwindConfig({ fallback: true }),
    button: color.button.toTailwindConfig({ fallback: true }),
    alpha: color.alpha.toTailwindConfig({ fallback: true }),
    avatar: color.avatar.toTailwindConfig({ fallback: true }),
    tag: color.tag.toTailwindConfig({ fallback: true }),
    overlay: color.overlay.toTailwindConfig({ fallback: true }),
    shadow: color.shadow.toPropertyRef({ fallback: true }),
    palette: paletteColor({ fallback: true }),
    inherit: 'inherit',
    current: 'currentColor',
    transparent: 'transparent',
  },
  ringOffsetColor: {
    DEFAULT: color.surface.get('default').toPropertyRef({ fallback: true }),
    border: color.border.toTailwindConfig({ fallback: true }),
    surface: color.surface.toTailwindConfig({ fallback: true }),
    content: color.content.toTailwindConfig({ fallback: true }),
    button: color.button.toTailwindConfig({ fallback: true }),
    alpha: color.alpha.toTailwindConfig({ fallback: true }),
    avatar: color.avatar.toTailwindConfig({ fallback: true }),
    tag: color.tag.toTailwindConfig({ fallback: true }),
    overlay: color.overlay.toTailwindConfig({ fallback: true }),
    shadow: color.shadow.toPropertyRef({ fallback: true }),
    focus: color.focus.toPropertyRef({ fallback: true }),
    palette: paletteColor({ fallback: true }),
    inherit: 'inherit',
    current: 'currentColor',
    transparent: 'transparent',
  },

  keyframes: {
    'den-shimmer': {
      '0%': {
        backgroundPosition: '-1000px 0',
      },
      to: {
        backgroundPosition: '1000px 0',
      },
    },
    slideIn: {
      from: { opacity: '0', transform: 'translateY(-100%)' },
      to: { opacity: '1', transform: 'translateY(0)' },
    },
    hide: {
      from: { opacity: '1' },
      to: { opacity: '0' },
    },
    slideRight: {
      from: { transform: 'translateX(var(--radix-toast-swipe-end-x))' },
      to: { transform: 'translateX(100%)' },
    },
    stack3DIn: {
      '0%': { transform: 'translate3d(0, 0, 0) scale(1)', top: '0' },
      '100%': {
        transform:
          'translate3d(0, var(--stack-y, 0), 0) scale(var(--stack-scale, 1))',
        top: 'var(--stack-y, 0)',
      },
    },
    mouseOverStack3D: {
      '0%': {
        transform:
          'translate3d(0, var(--stack-y, 0), 0) scale(var(--stack-scale, 1))',
      },
      '100%': {
        transform: 'translate3d(0, 0, 0) scale(1)',
        top: 'var(--stack-y, 0)',
      },
    },
    mouseOutStack3D: {
      '0%': {
        transform: 'translate3d(0, 0, 0) scale(1)',
      },
      '100%': {
        transform: 'translate3d(0, 0, 0) scale(var(--stack-scale, 1))',
        top: 'var(--stack-y, 0)',
      },
    },
    progressBar: {
      '0%': {
        transform: 'translateX(-100%)',
      },
      '100%': {
        transform: 'translateX(100%);',
      },
    },
    'loading-bar-indeterminate': {
      '0%': {
        transform: 'translateX(0) scaleX(0)',
      },
      '40%': {
        transform: 'translateX(0) scaleX(0.4)',
      },
      '100%': {
        transform: 'translateX(100%) scaleX(0.5)',
      },
    },
    spin: {
      to: {
        transform: 'rotate(360deg)',
      },
    },
  },
  // Animation and effects
  animation: {
    shimmer: 'den-shimmer 2s infinite linear',
    slideIn: 'slideIn 500ms cubic-bezier(0.16, 1, 0.3, 1)',
    hide: 'hide 300ms ease-in',
    slideRight: 'slideRight 300ms ease-out',
    stack3DIn: 'stack3DIn 500ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
    mouseOverStack3D: 'mouseOverStack3D 0.5s ease-out forwards',
    mouseOutStack3D: 'mouseOutStack3D 0.5s ease-out forwards',
    progressBar: 'progressBar 1s linear infinite',
    'loading-bar-indeterminate':
      'loading-bar-indeterminate 1.2s linear infinite',
    spin: 'spin 1s linear infinite',
  },
  backgroundImage: () => ({
    'gradient--45deg': 'linear-gradient(-45deg, var(--tw-gradient-stops))',
    'gradient--90deg': 'linear-gradient(-90deg, var(--tw-gradient-stops))',
    'gradient--180deg': 'linear-gradient(-180deg, var(--tw-gradient-stops))',
  }),
  blur: blur.toTailwindConfig({ fallback: true }),
  dropShadow: {},
  boxShadow: {
    none: '0 0 transparent',
    ...elevation.toTailwindConfig({ fallback: true }),
  },
  maxWidth: ({ theme, breakpoints }) => ({
    none: 'none',
    0: '0px',
    full: '100%',
    min: 'min-content',
    max: 'max-content',
    fit: 'fit-content',
    ...breakpoints(theme('screens')),
  }),
  spacing: {
    line: 'var(--line-height-md)',
    font: 'var(--font-size-md)',
    ...space.toTailwindConfig({ fallback: true }),
  },
  screens: screen,
  columns: {
    auto: 'auto',
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
    6: '6',
    7: '7',
    8: '8',
    9: '9',
    10: '10',
    11: '11',
    12: '12',
  },
  borderRadius: radius.toTailwindConfig({ fallback: true }),

  fontFamily: {
    ...fontFamily.toTailwindConfig({ fallback: false }),
  },
  fontSize: Object.fromEntries(
    Array.from(fontSize.keys()).map((key) => {
      return [
        key,
        [
          fontSize.get(key).toPropertyRef({ fallback: true }),
          lineHeight.get(key).toPropertyRef({ fallback: true }),
        ],
      ];
    }),
  ),
  fontWeight: fontWeight.toTailwindConfig({ fallback: true }),
  letterSpacing: {},
  lineHeight: {},

  ringWidth: {
    1: '1px',
    1.5: '1.5px',
    2: '2px',
    3: '3px',
    4: '4px',
    8: '8px',
  },
  outlineWidth: {
    1: '1px',
    1.5: '1.5px',
    2: '2px',
    3: '3px',
    4: '4px',
    8: '8px',
  },
  outlineOffset: {
    DEFAULT: '0px',
    0: '0px',
    1: '1px',
    2: '2px',
    3: '3px',
    4: '4px',
    8: '8px',
  },

  extend: {
    ringOpacity: {
      DEFAULT: '1',
    },

    typography: () => ({
      DEFAULT: {
        css: prose_css_defaults,
      },
    }),
  },

  variables: {
    DEFAULT: {
      ...radius.getPropertyDeclarations({ fallback: false }),
      ...elevation.getPropertyDeclarations({ fallback: false }),
      ...fontSize.getPropertyDeclarations({ fallback: false }),
      ...fontWeight.getPropertyDeclarations({ fallback: false }),
      ...lineHeight.getPropertyDeclarations({ fallback: false }),
      ...space.getPropertyDeclarations({ fallback: false }),
      ...fontFamily.getPropertyDeclarations({ fallback: false }),
      ...palette.blue.getPropertyDeclarations({ fallback: false }),
      ...palette.gray.getPropertyDeclarations({ fallback: false }),
      ...palette.green.getPropertyDeclarations({ fallback: false }),
      ...palette.orange.getPropertyDeclarations({ fallback: false }),
      ...palette.pink.getPropertyDeclarations({ fallback: false }),
      ...palette.purple.getPropertyDeclarations({ fallback: false }),
      ...palette.red.getPropertyDeclarations({ fallback: false }),
      ...palette.yellow.getPropertyDeclarations({ fallback: false }),
      ...color.border.getPropertyDeclarations({ fallback: false }),
      ...color.alpha.getPropertyDeclarations({ fallback: false }),
      ...color.avatar.getPropertyDeclarations({ fallback: false }),
      ...color.button.getPropertyDeclarations({ fallback: false }),
      ...color.content.getPropertyDeclarations({ fallback: false }),
      ...color.focus.getPropertyDeclarations({ fallback: false }),
      ...color.overlay.getPropertyDeclarations({ fallback: false }),
      ...color.shadow.getPropertyDeclarations({ fallback: false }),
      ...color.surface.getPropertyDeclarations({ fallback: false }),
      ...color.tag.getPropertyDeclarations({ fallback: false }),
    },
  },
} satisfies NonNullable<Config['theme']>;

Object.keys(theme.variables.DEFAULT).map((key, i, arr) => {
  if (arr.indexOf(key) !== i) {
    throw new Error(`Duplicate CSS variable encountered: ${key}`);
  }
  return undefined;
});
